import { AnimatePresence, motion } from 'framer-motion';
import { Check, X } from 'lucide-react';

import { useToast } from '@/stores/toast';
import { cn } from '@/lib/utils';

/**
 * Snackbar host — bottom-anchored toasts (success/error), matching the app's
 * recommendation/feedback snackbars. Mounted once in the root layout.
 */
export default function Toaster() {
  const toasts = useToast((s) => s.toasts);
  const dismiss = useToast((s) => s.dismiss);

  return (
    <div
      className="tw:pointer-events-none tw:absolute tw:inset-x-0 tw:bottom-0 tw:z-[100] tw:flex tw:flex-col tw:items-center tw:gap-2 tw:px-4 tw:pb-24"
      aria-live="polite"
    >
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.button
            key={t.id}
            type="button"
            onClick={() => dismiss(t.id)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            data-testid={`toast-${t.type}`}
            className={cn(
              'tw:pointer-events-auto tw:flex tw:w-full tw:max-w-[360px] tw:items-center tw:gap-3 tw:rounded-2xl tw:text-left tw:font-semibold tw:text-white tw:shadow-lg',
              t.icon ? 'tw:px-5 tw:py-4 tw:text-base' : 'tw:px-4 tw:py-3 tw:text-[15px]',
              // Same two colors as before, but taken from the tokens they were
              // hand-copied from rather than repeated as literals.
              t.type === 'success' ? 'tw:bg-green-3' : 'tw:bg-error',
            )}
          >
            {t.icon ? (
              <span className="tw:shrink-0 tw:text-[28px]" aria-hidden="true">
                {t.icon}
              </span>
            ) : (
              <span className="tw:flex tw:size-5 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-white/25">
                {t.type === 'success' ? (
                  <Check size={14} strokeWidth={3} />
                ) : (
                  <X size={14} strokeWidth={3} />
                )}
              </span>
            )}
            <span className="tw:flex-1">{t.message}</span>
          </motion.button>
        ))}
      </AnimatePresence>
    </div>
  );
}
